import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { motion } from 'framer-motion';
import { nip19 } from 'nostr-tools';
import { 
  Search,
  X,
  Disc3,
  User,
  Sparkles,
  TrendingUp,
  Music2,
  Clock,
  Users,
  Heart,
  Zap,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UploadDialog } from '@/components/UploadDialog';
import { TrackPreviewDialog } from '@/components/TrackPreviewDialog';
import { HeroGreeting } from '@/components/HeroGreeting';
import { HorizontalSection } from '@/components/HorizontalSection';
import { GenreFilters } from '@/components/GenreFilters';
import { TrackTile } from '@/components/TrackTile';
import { ProfileDropdown } from '@/components/ProfileDropdown';
import { LibrarySheet } from '@/components/LibrarySheet';
import { LoginArea } from '@/components/auth/LoginArea';
import { SupportAppDialog } from '@/components/SupportAppDialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMusicTracks } from '@/hooks/useMusicTracks';
import { useFollowingTracks } from '@/hooks/useFollowingTracks';
import { useFollowing } from '@/hooks/useFollowing';
import { useAuthor } from '@/hooks/useAuthor';
import { usePlayer } from '@/contexts/PlayerContext';
import { useLibrary } from '@/hooks/useLibrary';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

// Artist card component
function ArtistCard({ pubkey, index = 0 }: { pubkey: string; index?: number }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.03, duration: 0.3 }}
    >
      <Link 
        to={`/${npub}`} 
        className="flex flex-col items-center gap-3 w-24 sm:w-28 flex-shrink-0 group"
      >
        <div className="relative">
          <Avatar className={cn(
            'h-20 w-20 sm:h-24 sm:w-24 transition-all duration-300',
            'ring-2 ring-white/10 group-hover:ring-primary/50',
            'shadow-xl'
          )}>
            <AvatarImage src={metadata?.picture} className="object-cover" />
            <AvatarFallback className="bg-gradient-to-br from-white/10 to-white/5">
              <User className="h-8 w-8 text-white/30" />
            </AvatarFallback>
          </Avatar>
        </div>
        <span className="text-xs sm:text-sm text-center truncate w-full group-hover:text-primary transition-colors font-medium">
          {metadata?.name || metadata?.display_name || 'Artist'}
        </span>
      </Link>
    </motion.div>
  );
}

// Loading skeleton for track tiles
function TrackTileSkeleton() {
  return (
    <div className="w-36 sm:w-40 flex-shrink-0">
      <Skeleton className="aspect-square rounded-2xl mb-3" />
      <Skeleton className="h-4 w-3/4 mb-2" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

export default function Index() {
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allTracks, isLoading } = useMusicTracks(200);
  const { data: followingTracks, isLoading: followingLoading } = useFollowingTracks(50);
  const { data: following } = useFollowing();
  const { likedTrackIds } = useLibrary();
  const {
    currentTrack,
    setCurrentTrack,
    setPlaylist,
    isPlaying,
    togglePlay,
  } = usePlayer();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [previewTrack, setPreviewTrack] = useState<MusicTrack | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSupportApp, setShowSupportApp] = useState(false);

  useSeoMeta({
    title: 'Cove - Music on Nostr',
    description: 'Discover and stream music on Nostr. Support artists directly with Bitcoin.',
  });

  // Get genres with counts
  const genreData = useMemo(() => {
    if (!allTracks) return [];
    const counts = new Map<string, number>();
    allTracks.forEach(track => {
      track.genres.forEach(g => {
        const genre = g.toLowerCase();
        counts.set(genre, (counts.get(genre) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
  }, [allTracks]);

  // Get featured artists (most tracks)
  const featuredArtists = useMemo(() => {
    if (!allTracks) return [];
    const artistCounts = new Map<string, number>();
    allTracks.forEach(track => {
      artistCounts.set(track.event.pubkey, (artistCounts.get(track.event.pubkey) || 0) + 1);
    });
    return Array.from(artistCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([pubkey]) => pubkey);
  }, [allTracks]);

  // Track deduplication - tracks used in earlier sections won't appear in later ones
  const usedTrackIds = new Set<string>();

  const getUniqueTracks = (tracks: MusicTrack[], limit: number): MusicTrack[] => {
    const result: MusicTrack[] = [];
    for (const track of tracks) {
      if (!usedTrackIds.has(track.id) && result.length < limit) {
        usedTrackIds.add(track.id);
        result.push(track);
      }
    }
    return result;
  };

  // New Releases (most recent)
  const newReleases = useMemo(() => {
    if (!allTracks) return [];
    const sorted = [...allTracks].sort((a, b) => b.publishedAt - a.publishedAt);
    return getUniqueTracks(sorted, 15);
  }, [allTracks]);

  // Trending (random selection for now - simulated popularity)
  const trendingTracks = useMemo(() => {
    if (!allTracks) return [];
    // Shuffle and pick some tracks to simulate "trending"
    const shuffled = [...allTracks].sort(() => 0.5 - Math.random());
    return getUniqueTracks(shuffled, 15);
  }, [allTracks]);

  // Liked tracks
  const likedTracks = useMemo(() => {
    if (!allTracks || likedTrackIds.length === 0) return [];
    return allTracks.filter(t => likedTrackIds.includes(t.id)).slice(0, 15);
  }, [allTracks, likedTrackIds]);

  // Tracks from people you follow
  const followingUniqueTracks = useMemo(() => {
    if (!followingTracks) return [];
    return getUniqueTracks(followingTracks, 15);
  }, [followingTracks, newReleases]);

  // Discover tracks
  const discoverTracks = useMemo(() => {
    if (!allTracks) return [];
    const remaining = allTracks.filter(t => !usedTrackIds.has(t.id));
    const shuffled = [...remaining].sort(() => 0.5 - Math.random());
    return getUniqueTracks(shuffled, 15);
  }, [allTracks, newReleases, followingUniqueTracks]);

  // Filter tracks based on search and genres
  const filteredTracks = useMemo(() => {
    let tracks = allTracks || [];
    
    if (searchQuery) {
      tracks = tracks.filter(track =>
        track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        track.artist.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedGenres.length > 0) {
      tracks = tracks.filter(track =>
        track.genres.some(g => selectedGenres.includes(g.toLowerCase()))
      );
    }

    return tracks;
  }, [allTracks, searchQuery, selectedGenres]);

  // Update playlist when filtered tracks change
  useEffect(() => {
    if (filteredTracks.length > 0) {
      setPlaylist(filteredTracks);
    }
  }, [filteredTracks, setPlaylist]);



  const handleTrackSelect = (track: MusicTrack) => {
    if (currentTrack?.id === track.id) {
      togglePlay();
    } else {
      setCurrentTrack(track);
    }
  };

  const handleToggleGenre = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const isFiltering = searchQuery || selectedGenres.length > 0;
  const hasFollowingTracks = followingUniqueTracks && followingUniqueTracks.length > 0;
  const hasLikedTracks = likedTracks.length > 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/5 rounded-full blur-[80px]" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.5 }}>
              <Disc3 className="h-7 w-7 text-primary" />
            </motion.div>
            <span className="text-lg font-bold hidden sm:block">Cove</span>
          </Link>

          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 glass-input rounded-full h-10 text-sm border-white/10 focus:border-primary/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Library & Profile / Login */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {user && <LibrarySheet />}
            {user ? (
              <ProfileDropdown 
                onUploadClick={() => setShowUpload(true)}
                onAddAccountClick={() => setLoginDialogOpen(true)}
              />
            ) : (
              <LoginArea className="max-w-32" />
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative pb-32">
        {/* Hero Greeting */}
        <HeroGreeting />

        {/* Genre Filters */}
        {genreData.length > 0 && (
          <GenreFilters
            genres={genreData}
            selectedGenres={selectedGenres}
            onToggleGenre={handleToggleGenre}
            onClearAll={() => setSelectedGenres([])}
          />
        )}

        {/* Content */}
        {isFiltering ? (
          /* Filtered Results */
          <section className="px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold">
                  {searchQuery ? 'Search Results' : 'Filtered'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {filteredTracks.length} tracks
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedGenres([]); setSearchQuery(''); }}
                className="text-muted-foreground"
              >
                Clear
              </Button>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i}>
                    <Skeleton className="aspect-square rounded-2xl mb-3" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredTracks.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredTracks.map((track, index) => (
                  <TrackTile
                    key={track.id}
                    track={track}
                    onPlay={() => handleTrackSelect(track)}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                    isSelected={currentTrack?.id === track.id}
                    index={index}
                    className="w-full"
                  />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <Music2 className="h-12 w-12 mx-auto mb-4 text-white/10" />
                <p className="text-muted-foreground">No tracks found</p>
              </div>
            )}
          </section>
        ) : (
          /* Discovery Sections */
          <>
            {/* Your Library (Liked Tracks) */}
            {hasLikedTracks && (
              <HorizontalSection
                title="Your Library"
                icon={<Heart className="h-5 w-5" />}
                className="mb-6"
              >
                {likedTracks.map((track, index) => (
                  <TrackTile
                    key={track.id}
                    track={track}
                    onPlay={() => handleTrackSelect(track)}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                    isSelected={currentTrack?.id === track.id}
                    index={index}
                    size="sm"
                  />
                ))}
              </HorizontalSection>
            )}

            {/* New Releases */}
            {newReleases.length > 0 && (
              <HorizontalSection
                title="New Releases"
                icon={<Sparkles className="h-5 w-5 text-orange-500" />}
                className="mb-6"
              >
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => <TrackTileSkeleton key={i} />)
                ) : (
                  newReleases.map((track, index) => (
                    <TrackTile
                      key={track.id}
                      track={track}
                      onPlay={() => handleTrackSelect(track)}
                      isPlaying={currentTrack?.id === track.id && isPlaying}
                      isSelected={currentTrack?.id === track.id}
                      index={index}
                      size="sm"
                    />
                  ))
                )}
              </HorizontalSection>
            )}

            {/* Trending */}
            {trendingTracks.length > 0 && (
              <HorizontalSection
                title="Trending Now"
                icon={<TrendingUp className="h-5 w-5 text-pink-500" />}
                className="mb-6"
              >
                {trendingTracks.map((track, index) => (
                  <TrackTile
                    key={track.id}
                    track={track}
                    onPlay={() => handleTrackSelect(track)}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                    isSelected={currentTrack?.id === track.id}
                    index={index}
                    size="sm"
                  />
                ))}
              </HorizontalSection>
            )}

            {/* From People You Follow */}
            {hasFollowingTracks && (
              <HorizontalSection
                title="From Following"
                icon={<Users className="h-5 w-5" />}
                className="mb-6"
              >
                {followingLoading ? (
                  Array.from({ length: 6 }).map((_, i) => <TrackTileSkeleton key={i} />)
                ) : (
                  followingUniqueTracks.map((track, index) => (
                    <TrackTile
                      key={track.id}
                      track={track}
                      onPlay={() => handleTrackSelect(track)}
                      isPlaying={currentTrack?.id === track.id && isPlaying}
                      isSelected={currentTrack?.id === track.id}
                      index={index}
                      size="sm"
                    />
                  ))
                )}
              </HorizontalSection>
            )}

            {/* Discover */}
            {discoverTracks.length > 0 && (
              <HorizontalSection
                title="Discover"
                icon={<Sparkles className="h-5 w-5" />}
                className="mb-6"
              >
                {discoverTracks.map((track, index) => (
                  <TrackTile
                    key={track.id}
                    track={track}
                    onPlay={() => handleTrackSelect(track)}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                    isSelected={currentTrack?.id === track.id}
                    index={index}
                    size="sm"
                  />
                ))}
              </HorizontalSection>
            )}

            {/* Artists */}
            {featuredArtists.length > 0 && (
              <HorizontalSection
                title="Artists"
                icon={<TrendingUp className="h-5 w-5" />}
                className="mb-6"
              >
                {featuredArtists.map((pubkey, index) => (
                  <ArtistCard key={pubkey} pubkey={pubkey} index={index} />
                ))}
              </HorizontalSection>
            )}


          </>
        )}

        {/* Empty state */}
        {!isLoading && (!allTracks || allTracks.length === 0) && (
          <div className="py-20 text-center px-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl glass-card mb-6">
              <Music2 className="h-10 w-10 text-white/20" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No music yet</h2>
            <p className="text-muted-foreground mb-6">Be the first to share!</p>
            {user && (
              <Button onClick={() => setShowUpload(true)} className="rounded-full">
                Upload Track
              </Button>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-8 py-6 text-center">
          <button
            onClick={() => setShowSupportApp(true)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-primary transition-colors group"
          >
            <Heart className="h-3 w-3 group-hover:fill-current transition-all" />
            <span>Support the App</span>
            <Zap className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </footer>
      </main>

      {/* Upload Dialog */}
      <UploadDialog open={showUpload} onOpenChange={setShowUpload} />

      {/* Track Preview Dialog (for shared links) */}
      <TrackPreviewDialog
        track={previewTrack}
        open={showPreview}
        onClose={() => setShowPreview(false)}
        allTracks={allTracks}
      />

      {/* Support App Dialog */}
      <SupportAppDialog 
        open={showSupportApp} 
        onClose={() => setShowSupportApp(false)} 
      />
    </div>
  );
}
